import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Container, Spinner, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

function UploadedImages({ selectedDomain }) {
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [error, setError] = useState(null);
    const [selectedModel, setSelectedModel] = useState('gemini');

    useEffect(() => {
        setImages([]);
        setLoading(true);
        fetchImages();
    }, [selectedDomain]); // re-fetch whenever domain prop changes

    const fetchImages = async () => {
            try {
                const response = await fetch('/api/get_images');
                if (response.ok) {
                    const data = await response.json();
                    setImages(data.images);
                }
            } catch (err) {
                console.error("Error fetching images:", err);
            } finally {
                setLoading(false);
            }
        };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // const handleViewImage = async (url) => {
    //     try {
    //         const response = await fetch(url);
    //         if (!response.ok) throw new Error('Network response was not ok');
    //         const blob = await response.blob();
    //         const blobUrl = window.URL.createObjectURL(blob);
    //         window.open(blobUrl, '_blank');
    //     } catch (err) {
    //         console.error('Error opening image:', err);
    //         window.open(url, '_blank'); // Fallback
    //     }
    // };

    const uploadImage = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage) return;

        setError(null);
        setUploading(true);
        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        formData.append('model', selectedModel);
        
        try {
            const response = await fetch('/api/upload_image', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to upload image');
            }

            const data = await response.json();
            fetchImages(); // Refresh the collection after upload
            setShowUploadModal(false); 
            setImagePreview(null);
            setSelectedImage(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
        try {
            const response = await fetch(`/api/images/${id}/status?status=0`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: 0 }) // Mark as deleted
            });
            if (response.ok) {
                setImages(prev => prev.filter(img => img.id !== id));
            } else {
                alert("Failed to delete image");
            }
        } catch (err) {
            console.error("Error deleting image:", err);
            alert("Error deleting image: " + err.message);
        }
    };


    return (
        <Container className="collection-section py-4">
            <div className="text-center mb-5 position-relative d-flex justify-content-center align-items-center">
                <h2 className="section-title mb-0">Our Collection</h2>
                <div className="position-absolute end-0 d-flex gap-2">
                    <button className="btn btn-outline-secondary rounded-pill d-flex align-items-center gap-2" title="Batch Upload Images"
                        onClick={() => navigate('/batch-upload')}>
                        <span>Batch Upload</span>
                    </button>
                    <button className="btn btn-primary-custom rounded-pill pl-0 text-light d-flex align-items-center gap-2"title="Upload New Image"
                        onClick={() => setShowUploadModal(true)}>
                        <span className="d-inline-flex justify-content-center align-items-center bg-light text-dark rounded-circle"
                            style={{ width: "28px", height: "28px", fontSize: "18px"}}> +</span>
                        <span>Add</span>
                    </button>
                </div>
            </div>

            {loading && (
                <div className="d-flex justify-content-center my-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {images.length === 0 ? (
                <div className="text-center text-muted my-5">
                    <p>No images found in the collection.</p>
                </div>
            ) : (
                <Row xs={2} sm={3} md={4} lg={5} xl={6} className="gx-4 gy-5 justify-content-center">
                    {images.map((img, index) => (
                        <Col key={index}>
                            <Card 
                                className="collection-card h-100 shadow-sm border-0 bg-transparent">
                            <div className="collection-img-wrapper" style={{ 
                                position: 'relative', 
                                height: '160px', 
                                overflow: 'hidden', 
                                borderRadius: '15px',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'}}>
                                <Card.Img 
                                    variant="top" 
                                    src={img.url} 
                                    alt={img.name} 
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'contain',
                                        transition: 'transform 0.5s ease'}}
                                        className="collection-img" />
                                <div className="img-overlay">
                                    <span className="view-text" onClick={() => navigate(`/details/${encodeURIComponent(img.name)}`)}
                                        style={{ cursor: 'pointer' }}>View</span>
                                    <span className="delete-icon" onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id, img.name); }}
                                        style={{ 
                                            position: 'absolute', 
                                            bottom: '10px', 
                                            right: '15px', 
                                            color: '#a00202ff', 
                                            cursor: 'pointer', 
                                            fontSize: '1.25rem',
                                            transition: 'color 0.2s'
                                        }}
                                        title="Delete Image">
                                        <i className="bi bi-trash-fill"></i>
                                    </span>
                                </div>
                            </div>
                            <Card.Body className="p-2 text-center">
                                <small className="text-muted d-block text-truncate mb-2" style={{ fontSize: '0.75rem' }}>
                                    {img.name}
                                </small>
                                
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>
            )}

            {showUploadModal && (
                <Modal show={showUploadModal} onHide={() => { setImagePreview(null); setShowUploadModal(false); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fs-6'>Upload New Image</Modal.Title>
                </Modal.Header>

                <Modal.Body className='m-4'>
                     <div className="upload-dropzone w-75 mx-auto p-4">
                        <input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageChange} 
                            // disabled={loading && textQuery.trim()}
                        />
                        <div className="py-2">
                            {imagePreview ? (
                                <img 
                                    src={imagePreview} 
                                    alt="Preview" 
                                    style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} 
                                />
                            ) : (
                                 <div className="text-muted">
                                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>+</div>
                                    <p className="mb-0">Click or drag image to upload</p>
                                    <small>JPG, PNG or WEBP</small>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mb-3 w-75 mx-auto py-4">
                        <label className="form-label text-muted small fw-bold">Select Model</label>
                        <select 
                            className="form-select rounded-pill" 
                            value={selectedModel} 
                            onChange={(e) => setSelectedModel(e.target.value)}>
                            <option  value="gemini" className={`${selectedModel === 'gemini' ? 'd-none' : ''}`}>Gemini</option>
                            <option value="qwen" className={`${selectedModel === 'qwen' ? 'd-none' : ''}`}>Qwen</option>
                    
                        </select>
                    </div>
                    <Button className='btn-primary-custom rounded-pill mt-3 px-4 mx-auto d-block' disabled={!selectedImage} onClick={uploadImage}>{uploading ? <span><Spinner animation="border" size="sm" /> Uploading...</span>: 'Upload'}</Button>
                </Modal.Body>
            </Modal>
            )}
        </Container>
    );
}

export default UploadedImages;